import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ClipboardList,
    Pencil,
    PencilLine,
    Trash2,
    PlusCircle,
    Calendar,
    CheckCheck,
    Copy,
    MoreHorizontal,
    Eye,
    MessageSquare,
    User,
    XCircle
} from 'lucide-react';
import {
    makeStyles,
    shorthands,
    tokens,
    Text,
    Button,
    mergeClasses,
    Title1,
    Menu,
    MenuTrigger,
    MenuList,
    MenuItem,
    MenuPopover
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import SyButton from '../Common/SyButton';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { SurveyHeader, SurveyStatus, ProblemDetails } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import SurveyMiniFormDialog from './SurveyMiniFormDialog';
import SurveyDuplicateDialog from './SurveyDuplicateDialog';
import SurveyDeleteDialog from './SurveyDeleteDialog';
import SurveyCloseDialog from './SurveyCloseDialog';
import SurveyActivateDialog from './SurveyActivateDialog';
import { SurveyFormData } from './SurveyMiniForm';
import { useNotification } from '../../Contexts/NotificationContext';
import { buildRoute, SettingsSurveysNewRoute, SettingsSurveysEditRoute, SettingsSurveysViewRoute } from '../../Routes';
import { useTheme } from '../../store/hooks';
import { DARK_THEME } from '../../store/models/theme';
import { HorizontalSeparatorLine } from '../Common/HorizontalSeparatorLine';

const useStyles = makeStyles({
    container: {
        background: 'var(--border-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.padding('24px'),
        ...shorthands.gap('10px'),
        boxSizing: 'border-box',
        /* allow container to shrink instead of forcing full height which can create an unwanted scrollbar */
        minHeight: '0',
        /* allow this container to take remaining vertical space so footers or other page chrome are pushed to the bottom */
        flex: '1 1 auto',
        width: '100%',
        ...shorthands.overflow('auto'),
    },


    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        ...shorthands.gap('16px'),
        width: '100%',
        maxWidth: '960px',
        paddingBottom: '24px',
    },
    title: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: '18px',
        lineHeight: 'normal',
        letterSpacing: '0.09px',
        color: tokens.colorNeutralForeground1,
        textAlign: 'center',
        whiteSpace: 'nowrap',
    },
    countBadge: {
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius('6px'),
        ...shorthands.padding('4px', '8px'),
        backgroundColor: tokens.colorNeutralBackground1,
        fontSize: '13px',
        fontWeight: 400,
        color: tokens.colorNeutralForeground2,
        whiteSpace: 'nowrap',
    },
    surveyCard: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.border('1px', 'solid', 'var(--color-gray-100)'),
        ...shorthands.borderRadius('8px'),
        width: '100%',
        maxWidth: '900px',
        height: 'auto',
        boxSizing: 'border-box',
        ...shorthands.transition('box-shadow', '0.2s', 'ease'),
        ':hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
    },
    surveyCardContent: {
        display: 'flex',
        background: 'var(--colorCardBackground1)',
        ...shorthands.gap('16px'),
        alignItems: 'start',
        ...shorthands.padding('17px', '20px'),
        boxSizing: 'border-box',
        width: '100%',
        ...shorthands.borderRadius('inherit'),
        flexWrap: 'wrap',
        '@media (min-width: 768px)': {
            ...shorthands.gap('32px'),
            flexWrap: 'nowrap',
        },
    },
    clipboardIcon: {
        width: '20px',
        height: '20px',
        flexShrink: 0,
        color: 'var(--color-primary-500)',
    },
    leftSection: {
        display: 'flex',
        ...shorthands.gap('0px'),
        alignItems: 'start',
        flexGrow: 1,
        flexBasis: 0,
        minWidth: '200px',
        minHeight: '1px',
    },
    contentMiddle: {
        display: 'flex',
        flexGrow: 1,
        flexBasis: 0,
        minWidth: '1px',
        minHeight: '1px',
        alignItems: 'start',
        justifyContent: 'space-between',
    },
    surveyInfo: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('12px'),
        flexGrow: 1,
        flexBasis: 0,
        minWidth: '1px',
        minHeight: '1px',
    },
    surveyHeader: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('4px'),
        width: '100%',
    },
    surveyNameRow: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('4px'),
        width: '100%',
    },
    surveyName: {
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.08px',
        color: tokens.colorNeutralForeground1,
        flexShrink: 1,
        cursor: 'pointer',
        ...shorthands.overflow('hidden'),
        textOverflow: 'ellipsis',
        ...shorthands.transition('color', '0.2s', 'ease'),
        ':hover': {
            color: 'var(--color-primary-600)',
        },
        '@media (max-width: 767px)': {
            whiteSpace: 'normal',
        },
        '@media (min-width: 768px)': {
            whiteSpace: 'nowrap',
        },
    },
    editIconSmall: {
        width: '10.667px',
        height: '10.667px',
        color: tokens.colorNeutralForeground3,
        flexShrink: 0,
        cursor: 'pointer',
        '&:hover': {
            color: tokens.colorNeutralForeground2,
        },
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        ...shorthands.padding('var(--spacing-1)', 'var(--spacing-2)'),
        ...shorthands.borderRadius('var(--radius-full)'),
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    badgeActive: {
        backgroundColor: 'var(--color-green-100)',
    },
    badgeClosed: {
        backgroundColor: 'var(--color-gray-100)',
    },
    badgeDraft: {
        backgroundColor: 'var(--color-amber-100)',
    },
    badgeText: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: '0.75rem',
        lineHeight: '16px',
        letterSpacing: '0.05px',
        color: '#ffffff',
        ...shorthands.padding('0px', '2.5px'),
    },
    badgeTextClosed: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: '0.75rem',
        lineHeight: '16px',
        letterSpacing: '0.05px',
        color: 'var(--color-gray-600)',
        ...shorthands.padding('0px', '2.5px'),
    },
    badgeTextDraft: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: '0.75rem',
        lineHeight: '16px',
        letterSpacing: '0.05px',
        color: 'var(--color-amber-700)',
        ...shorthands.padding('0px', '2.5px'),
    },
    badgeTextActive: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: '0.75rem',
        lineHeight: '16px',
        letterSpacing: '0.05px',
        color: 'var(--color-green-700)',
        ...shorthands.padding('0px', '2.5px'),
    },
    surveyDescription: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.07px',
        color: '#6b7280',
        width: '100%',
        minWidth: '100%',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
    },
    spacer: {
        backgroundColor: 'transparent',
        height: '4px',
        width: '28px',
        flexShrink: 0,
    },
    metadataRow: {
        display: 'flex',
        ...shorthands.gap('8px'),
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
    },
    metadataItem: {
        display: 'flex',
        ...shorthands.gap('4px'),
        alignItems: 'center',
        minWidth: 'fit-content',
        flexShrink: 0,
    },
    metadataIcon: {
        width: '20px',
        height: '20px',
        color: tokens.colorNeutralForeground2,
        flexShrink: 0,
    },
    metadataIconSmall: {
        width: '14px',
        height: '14px',
        color: tokens.colorNeutralForeground2,
        flexShrink: 0,
    },
    metadataIconGreen: {
        width: '16.667px',
        height: '16.667px',
        color: '#16a34a',
        flexShrink: 0,
    },
    metadataIconRed: {
        width: '16.667px',
        height: '16.667px',
        color: 'var(--color-red-500)',
        flexShrink: 0,
    },
    metadataText: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.07px',
        color: tokens.colorNeutralForeground2,
        flexShrink: 0,
        '@media (max-width: 767px)': {
            fontSize: '12px',
        },
    },
    metadataTextSmall: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        fontSize: '0.75rem',
        lineHeight: '20px',
        letterSpacing: '0.07px',
        color: 'var(--color-gray-500)',
        flexShrink: 0,
    },
    metadataSeparator: {
        height: '12px',
        width: '0px',
        position: 'relative',
        flexShrink: 0,
        '::before': {
            content: '""',
            position: 'absolute',
            inset: '-4.17% -0.5px',
            backgroundColor: tokens.colorNeutralStroke2,
            width: '1px',
            left: '0',
        },
    },
    actionsContainer: {
        display: 'flex',
        ...shorthands.gap('10px'),
        alignItems: 'center',
        width: '100%',
        flexShrink: 0,
        justifyContent: 'flex-end',
        '@media (min-width: 768px)': {
            width: 'auto',
            minWidth: '225px',
        },
    },
    primaryButtonContainer: {
        position: 'relative',
        flexShrink: 0,
    },
    overlappingButton: {
        position: 'absolute',
        top: 0,
        left: 0,
    },
    actionsButtonGroup: {
        display: 'flex',
        ...shorthands.gap('8px'),
        alignItems: 'center',
        flexShrink: 0,
    },
    actionIcon: {
        width: '16px',
        height: '16px',
    },
    deleteIcon: {
        color: 'var(--color-red-500)',
    },
    dotMenuButton: {
        ':hover': {
            color: 'var(--color-gray-500)',
        },
    },
    menuItem: {
        '& svg': {
            color: `${tokens.colorNeutralForeground2} !important`,
        },
        ':hover svg': {
            color: `${tokens.colorNeutralForeground2} !important`,
        },
    },
    menuItemDelete: {
        color: 'var(--color-red-500) !important',
        '& svg': {
            color: 'var(--color-red-500) !important',
        },
        ':hover': {
            color: 'var(--color-red-500) !important',
        },
        ':hover svg': {
            color: 'var(--color-red-500) !important',
        },
    },
});

const SurveyList: FC = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { dispatchNotification } = useNotification();
    const { theme } = useTheme();
    const [surveys, setSurveys] = useState<SurveyHeader[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [editingMiniSurveyId, setEditingMiniSurveyId] = useState<string | null>(null);
    const [duplicatingSurveyId, setDuplicatingSurveyId] = useState<string | null>(null);
    const [duplicatingSurveyName, setDuplicatingSurveyName] = useState<string | null>(null);
    const [deletingSurveyId, setDeletingSurveyId] = useState<string | null>(null);
    const [deletingSurveyName, setDeletingSurveyName] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [closingSurveyId, setClosingSurveyId] = useState<string | null>(null);
    const [closingSurveyName, setClosingSurveyName] = useState<string | null>(null);
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const [activatingSurveyId, setActivatingSurveyId] = useState<string | null>(null);
    const [activatingSurveyName, setActivatingSurveyName] = useState<string | null>(null);
    const [isActivating, setIsActivating] = useState<boolean>(false);

    // Function to fetch surveys
    const fetchSurveys = async (abortController: AbortController) => {
        setIsLoading(true);
        await performEnterpriseGPTApiCall(async (client, signal) => {
            try {
                const surveyHeaders = await client.getAllSurveyHeaders(signal);
                setSurveys(surveyHeaders || []);
            } catch (error) {
                console.error('Failed to fetch survey headers:', error);
                setSurveys([]);
            } finally {
                setIsLoading(false);
            }
        }, { abortController });
    };

    // Fetch surveys on component mount
    useEffect(() => {
        const abortController = new AbortController();
        fetchSurveys(abortController);

        return () => {
            abortController.abort();
        };
    }, []);

    // Helper function to format date
    const formatDate = (date: Date | null | undefined): string => {
        if (!date) return '-';
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Helper function to check if survey is active
    const isActive = (status?: SurveyStatus): boolean => {
        return status === SurveyStatus.Active;
    };

    // Helper function to check if survey is closed
    const isClosed = (status?: SurveyStatus): boolean => {
        return status === SurveyStatus.Closed;
    };

    // Helper function to check if survey is in draft
    const isDraft = (status?: SurveyStatus): boolean => {
        return status === SurveyStatus.Draft;
    };

    // Handler for creating a new survey (not needed anymore since we navigate to /settings/surveys/new)
    // Kept for potential future use with mini dialog if needed

    // Handler for editing a mini survey (from the small edit dialog)
    const handleEditMiniSurvey = () => {
        // Close dialog and refresh the survey list
        setEditingMiniSurveyId(null);
        // Refetch the survey list
        const abortController = new AbortController();
        fetchSurveys(abortController);
    };

    // Handler for opening the small edit dialog (mini form)
    const handleOpenEditDialog = (survey: SurveyHeader) => {
        setEditingMiniSurveyId(survey.id || null);
    };

    // Handler for opening activate survey dialog
    const handleOpenActivateDialog = (survey: SurveyHeader) => {
        setActivatingSurveyId(survey.id || null);
        setActivatingSurveyName(survey.title || null);
    };

    const handleCloseActivateDialog = () => {
        setActivatingSurveyId(null);
        setActivatingSurveyName(null);
        setIsActivating(false);
    };

    // Handler for confirming activate survey
    const handleConfirmActivateSurvey = async () => {
        if (!activatingSurveyId) return;

        setIsActivating(true);
        await performEnterpriseGPTApiCall(async (client, signal) => {
            try {
                await client.activateSurvey(activatingSurveyId, signal);
                dispatchNotification(
                    t('adminSettings.surveys.activateSuccess', 'Survey activated successfully'),
                    'success'
                );
                handleCloseActivateDialog();
                // Refetch surveys after activation
                const abortController = new AbortController();
                fetchSurveys(abortController);
            } catch (error) {
                console.error('Failed to activate survey:', error);

                // Check if it's a 409 Conflict error with a specific message
                if (error instanceof ProblemDetails && error.status === 409 && error.detail) {
                    dispatchNotification(error.detail, 'error');
                } else {
                    dispatchNotification(
                        t('adminSettings.surveys.activateError', 'Failed to activate survey. Please try again.'),
                        'error'
                    );
                }
                setIsActivating(false);
            }
        });
    };

    // Handler for opening close survey dialog
    const handleOpenCloseDialog = (survey: SurveyHeader) => {
        setClosingSurveyId(survey.id || null);
        setClosingSurveyName(survey.title || null);
    };

    const handleCloseCloseDialog = () => {
        setClosingSurveyId(null);
        setClosingSurveyName(null);
        setIsClosing(false);
    };

    // Handler for confirming close survey
    const handleConfirmCloseSurvey = async () => {
        if (!closingSurveyId) return;

        setIsClosing(true);
        await performEnterpriseGPTApiCall(async (client, signal) => {
            try {
                await client.closeSurvey(closingSurveyId, signal);
                dispatchNotification(
                    t('adminSettings.surveys.closeSuccess', 'Survey closed successfully'),
                    'success'
                );
                handleCloseCloseDialog();
                // Refetch surveys after closing
                const abortController = new AbortController();
                fetchSurveys(abortController);
            } catch (error) {
                console.error('Failed to close survey:', error);

                // Check if it's a 409 Conflict error with a specific message
                if (error instanceof ProblemDetails && error.status === 409 && error.detail) {
                    dispatchNotification(error.detail, 'error');
                } else {
                    dispatchNotification(
                        t('adminSettings.surveys.closeError', 'Failed to close survey. Please try again.'),
                        'error'
                    );
                }
                setIsClosing(false);
            }
        });
    };

    const handleViewSurvey = (surveyId: string) => {
        navigate(buildRoute(SettingsSurveysViewRoute, { id: surveyId }));
    };

    const handleOpenDeleteDialog = (survey: SurveyHeader) => {
        setDeletingSurveyId(survey.id || null);
        setDeletingSurveyName(survey.title || null);
    };

    const handleCloseDeleteDialog = () => {
        setDeletingSurveyId(null);
        setDeletingSurveyName(null);
        setIsDeleting(false);
    };

    const handleConfirmDelete = async () => {
        if (!deletingSurveyId) return;

        setIsDeleting(true);
        await performEnterpriseGPTApiCall(async (client, signal) => {
            try {
                await client.deleteSurvey(deletingSurveyId, signal);
                dispatchNotification(t('adminSettings.surveys.deleteSuccess', 'Survey deleted successfully'), 'success');
                handleCloseDeleteDialog();
                const ac = new AbortController();
                fetchSurveys(ac);
            } catch (error) {
                console.error('Failed to delete survey:', error);
                dispatchNotification(t('adminSettings.surveys.deleteError', 'Failed to delete survey'), 'error');
                setIsDeleting(false);
            }
        });
    };

    // Helper function to sort surveys
    const sortedSurveys = [...surveys].sort((a, b) => {
        // First, group by status: Active (1), Draft (2), Closed (3)
        const getStatusPriority = (status?: SurveyStatus): number => {
            if (isActive(status)) return 1;
            if (isDraft(status)) return 2;
            if (isClosed(status)) return 3;
            return 4;
        };

        const aPriority = getStatusPriority(a.status);
        const bPriority = getStatusPriority(b.status);

        // Sort by status group first
        if (aPriority !== bPriority) {
            return aPriority - bPriority;
        }

        // Within Closed surveys, sort by closedDate if available
        if (isClosed(a.status) && isClosed(b.status)) {
            const aHasClosedDate = a.closedDate != null;
            const bHasClosedDate = b.closedDate != null;

            // Prioritize surveys with closedDate
            if (aHasClosedDate && !bHasClosedDate) return -1;
            if (!aHasClosedDate && bHasClosedDate) return 1;

            // Both have closedDate - sort by closedDate (newest first)
            if (aHasClosedDate && bHasClosedDate) {
                return new Date(b.closedDate!).getTime() - new Date(a.closedDate!).getTime();
            }

            // Both don't have closedDate - fall through to lastChangeDate
        }

        // Within the same status group (non-Closed or Closed without closedDate), sort by lastChangeDate (newest first)
        const aHasDate = a.lastChangeDate != null;
        const bHasDate = b.lastChangeDate != null;

        if (aHasDate && bHasDate) {
            return new Date(b.lastChangeDate!).getTime() - new Date(a.lastChangeDate!).getTime();
        }

        // If one has a date and the other doesn't, prioritize the one with a date
        if (aHasDate && !bHasDate) return -1;
        if (!aHasDate && bHasDate) return 1;

        // Both don't have lastChangeDate - sort by id (bigger id first)
        return (b.id || '').localeCompare(a.id || '');
    });

    if (isLoading) {
        return (
            <div className={classes.container}>
                <div className="settings-skeleton-stack">
                    <div className="react-loading-skeleton settings-skeleton-card h-220 w-80p" />
                    <div className="react-loading-skeleton settings-skeleton-card h-220 w-80p" />
                    <div className="react-loading-skeleton settings-skeleton-card h-220 w-80p" />
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="titleSection">
                <div className="titleSection-content">
                    <ClipboardList size={20} className="titleSection-userIcon" />
                    <Title1 className="settingsTitle">
                        {t('adminSettings.surveys.title', 'Surveys')}
                    </Title1>
                </div>
                <SyButton
                    appearance="primary"
                    icon={<PlusCircle size={14} />}
                    onClick={() => navigate(SettingsSurveysNewRoute)}
                    className="button-margin-right compact-survey-button"
                >
                    {t('adminSettings.surveys.createButton', 'New Survey')}
                </SyButton>
            </div>

            <HorizontalSeparatorLine />


            <div className={classes.container}>

                <div className={classes.contentWrapper}>
                    {/* Removed the count from here as it is now in the header */}

                    {sortedSurveys.map((survey) => (
                        <div key={`${survey.id}_${survey.version}`} className={classes.surveyCard} style={theme === DARK_THEME ? { border: 'none' } : undefined}>
                            <div className={classes.surveyCardContent}>
                                <div className={classes.leftSection}>
                                    <div className={classes.contentMiddle}>
                                        <div className={classes.surveyInfo}>
                                            <div className={classes.surveyHeader}>
                                                <div className={classes.surveyNameRow}>
                                                    <Text
                                                        className={classes.surveyName}
                                                        onClick={() => {
                                                            if (survey.id) {
                                                                if (isDraft(survey.status)) {
                                                                    navigate(buildRoute(SettingsSurveysEditRoute, { id: survey.id }));
                                                                } else {
                                                                    handleViewSurvey(survey.id);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        {survey.title || 'Untitled Survey'}
                                                    </Text>
                                                    {/* Removed inline Pencil icon */}

                                                    {isDraft(survey.status) && (
                                                        <div className={mergeClasses(classes.badge, classes.badgeDraft)}>
                                                            <Text className={classes.badgeTextDraft}>
                                                                Draft
                                                            </Text>
                                                        </div>
                                                    )}
                                                    {isActive(survey.status) && (
                                                        <div className={mergeClasses(classes.badge, classes.badgeActive)}>
                                                            <Text className={classes.badgeTextActive}>
                                                                Active
                                                            </Text>
                                                        </div>
                                                    )}
                                                    {isClosed(survey.status) && (
                                                        <div className={mergeClasses(classes.badge, classes.badgeClosed)}>
                                                            <Text className={classes.badgeTextClosed}>
                                                                Closed
                                                            </Text>
                                                        </div>
                                                    )}
                                                </div>
                                                <Text className={classes.surveyDescription}>
                                                    {survey.description || 'No description available'}
                                                </Text>
                                                <div className={classes.spacer} />
                                                <div className={classes.metadataRow}>
                                                    {/* Removed Last update date */}

                                                    {/* Show replies count for all surveys if available (usually non-draft) */}
                                                    {(survey as any).totalResponses !== undefined && (
                                                        <div className={classes.metadataItem}>
                                                            <MessageSquare className={classes.metadataIconSmall} />
                                                            <Text className={classes.metadataTextSmall} >
                                                                {(survey as any).totalResponses} replies
                                                            </Text>
                                                        </div>
                                                    )}

                                                    {isActive(survey.status) && survey.activationDate && (
                                                        <>
                                                            {(survey as any).totalResponses !== undefined && <div className={classes.metadataSeparator} />}
                                                            <div className={classes.metadataItem}>
                                                                <CheckCheck className={classes.metadataIconGreen} size={20} />
                                                                <Text className={classes.metadataTextSmall}>
                                                                    Activated on :
                                                                </Text>
                                                                <Text className={classes.metadataTextSmall}>
                                                                    {formatDate(survey.activationDate)}
                                                                </Text>
                                                            </div>
                                                        </>
                                                    )}

                                                    {isClosed(survey.status) && survey.closedDate && (
                                                        <>
                                                            {(survey as any).totalResponses !== undefined && <div className={classes.metadataSeparator} />}
                                                            <div className={classes.metadataItem}>
                                                                <XCircle className={classes.metadataIconRed} size={20} />
                                                                <Text className={classes.metadataTextSmall}>
                                                                    Closed on :
                                                                </Text>
                                                                <Text className={classes.metadataTextSmall}>
                                                                    {formatDate(survey.closedDate)}
                                                                </Text>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={classes.actionsContainer}>
                                    <div className={classes.primaryButtonContainer}>
                                        <SyButton
                                            appearance="primary"
                                            style={{ visibility: isDraft(survey.status) || isClosed(survey.status) ? 'visible' : 'hidden' }}
                                            onClick={() => handleOpenActivateDialog(survey)}
                                        >
                                            Activate
                                        </SyButton>
                                        <SyButton
                                            appearance="tertiary"
                                            className={classes.overlappingButton}
                                            style={{ visibility: isActive(survey.status) ? 'visible' : 'hidden' }}
                                            onClick={() => handleOpenCloseDialog(survey)}
                                        >
                                            Close
                                        </SyButton>
                                    </div>

                                    <Menu>
                                        <MenuTrigger disableButtonEnhancement>
                                            <SyButton
                                                appearance="ghost"
                                                icon={<MoreHorizontal size={16} />}
                                                aria-label="More actions"
                                                iconOnly
                                            />
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                {isDraft(survey.status) || isClosed(survey.status) ? (
                                                    <>
                                                        <MenuItem
                                                            className={classes.menuItem}
                                                            icon={<PencilLine size={16} />}
                                                            onClick={() => survey.id && navigate(buildRoute(SettingsSurveysEditRoute, { id: survey.id }))}
                                                        >
                                                            Edit
                                                        </MenuItem>
                                                        <MenuItem
                                                            className={classes.menuItem}
                                                            icon={<Copy size={16} />}
                                                            onClick={() => { if (survey.id) { setDuplicatingSurveyId(survey.id); setDuplicatingSurveyName(survey.title || ''); } }}
                                                        >
                                                            Duplicate
                                                        </MenuItem>
                                                        <MenuItem
                                                            className={classes.menuItemDelete}
                                                            icon={<Trash2 size={16} />}
                                                            onClick={() => handleOpenDeleteDialog(survey)}
                                                        >
                                                            Delete
                                                        </MenuItem>
                                                    </>
                                                ) : (
                                                    <MenuItem
                                                        className={classes.menuItem}
                                                        icon={<Eye size={16} />}
                                                        onClick={() => survey.id && handleViewSurvey(survey.id)}
                                                    >
                                                        View
                                                    </MenuItem>
                                                )}
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <SurveyMiniFormDialog
                    open={editingMiniSurveyId !== null}
                    onClose={() => setEditingMiniSurveyId(null)}
                    onSubmit={handleEditMiniSurvey}
                    mode="edit"
                    surveyId={editingMiniSurveyId || undefined}
                />

                <SurveyDuplicateDialog
                    open={duplicatingSurveyId !== null}
                    sourceSurveyId={duplicatingSurveyId || undefined}
                    sourceSurveyName={duplicatingSurveyName || undefined}
                    onClose={() => { setDuplicatingSurveyId(null); setDuplicatingSurveyName(null); }}
                    onSuccess={() => { const ac = new AbortController(); fetchSurveys(ac); }}
                />

                <SurveyDeleteDialog
                    open={deletingSurveyId !== null}
                    surveyName={deletingSurveyName || undefined}
                    onClose={handleCloseDeleteDialog}
                    onConfirm={handleConfirmDelete}
                    isDeleting={isDeleting}
                />

                <SurveyCloseDialog
                    open={closingSurveyId !== null}
                    surveyName={closingSurveyName || undefined}
                    onClose={handleCloseCloseDialog}
                    onConfirm={handleConfirmCloseSurvey}
                    isClosing={isClosing}
                />

                <SurveyActivateDialog
                    open={activatingSurveyId !== null}
                    surveyName={activatingSurveyName || undefined}
                    onClose={handleCloseActivateDialog}
                    onConfirm={handleConfirmActivateSurvey}
                    isActivating={isActivating}
                />
            </div>
        </div>
    );
};

export default SurveyList;
